import { Controller } from "@hotwired/stimulus"

// Cascading dropdown UI for fit-rule conditions:
//   field → match type → values
//
// Each <option> in the field <select> carries:
//   data-match-types='["equals","in_list"]'   (allowed match types)
//   data-options='["France","FR",...]'        (predefined value list, or [])
//   data-has-options="true|false"
//
// Match-type labels come from the `data-match-type-labels` JSON on the wrapper.
// Value-input <div data-condition-builder-target="valueGroup" data-match-type="..."/>
// blocks are pre-rendered; we just toggle `hidden` and refill any <select> children
// with the field's options when relevant.
//
// Toggling "Advanced (raw JSON)" swaps the structured area for a textarea
// and disables/enables the right inputs so only one mode submits.
export default class extends Controller {
  static targets = [
    "field",
    "matchType",
    "valueGroup",
    "rawToggle",
    "rawArea",
    "structuredArea"
  ]

  static values = {
    matchTypeLabels: Object
  }

  connect() {
    this.refreshMatchTypes()
    this.refreshValueInputs()
    this.applyMode()
  }

  fieldChanged() {
    this.refreshMatchTypes()
    this.refreshValueInputs()
  }

  matchTypeChanged() {
    this.refreshValueInputs()
  }

  toggleRaw() {
    this.applyMode()
  }

  // --- internals --------------------------------------------------------

  selectedFieldOption() {
    return this.fieldTarget.options[this.fieldTarget.selectedIndex]
  }

  refreshMatchTypes() {
    const opt = this.selectedFieldOption()
    if (!opt) return
    const allowed = JSON.parse(opt.dataset.matchTypes || "[]")
    const previousValue = this.matchTypeTarget.value

    this.matchTypeTarget.innerHTML = ""
    for (const key of allowed) {
      const o = document.createElement("option")
      o.value = key
      o.textContent = this.matchTypeLabelsValue[key] || key
      this.matchTypeTarget.appendChild(o)
    }

    // Try to keep previous selection if still valid; else fall back to first.
    if (allowed.includes(previousValue)) this.matchTypeTarget.value = previousValue
  }

  refreshValueInputs() {
    const opt = this.selectedFieldOption()
    if (!opt) return
    const matchType = this.matchTypeTarget.value
    const fieldOptions = JSON.parse(opt.dataset.options || "[]")
    const hasOptions = opt.dataset.hasOptions === "true"

    for (const group of this.valueGroupTargets) {
      const visible = group.dataset.matchType === matchType
      group.hidden = !visible

      // Populate <select> children with field options when this group is visible.
      if (visible && hasOptions) {
        for (const select of group.querySelectorAll("select[data-condition-builder-options-target='1']")) {
          const previous = Array.from(select.selectedOptions).map((o) => o.value)
          const placeholder = select.dataset.placeholder

          select.innerHTML = ""
          if (placeholder && !select.multiple) {
            const o = document.createElement("option")
            o.value = ""
            o.textContent = placeholder
            select.appendChild(o)
          }
          for (const val of fieldOptions) {
            const o = document.createElement("option")
            o.value = val
            o.textContent = val
            if (previous.includes(val)) o.selected = true
            select.appendChild(o)
          }
        }
      }
    }
  }

  applyMode() {
    const raw = this.hasRawToggleTarget && this.rawToggleTarget.checked
    if (this.hasRawAreaTarget) this.rawAreaTarget.hidden = !raw
    if (this.hasStructuredAreaTarget) this.structuredAreaTarget.hidden = raw

    // Disable the inactive side so it doesn't submit ghost params.
    if (this.hasStructuredAreaTarget) {
      for (const el of this.structuredAreaTarget.querySelectorAll("input, select, textarea")) {
        el.disabled = raw
      }
    }
    if (this.hasRawAreaTarget) {
      for (const el of this.rawAreaTarget.querySelectorAll("input, select, textarea")) {
        el.disabled = !raw
      }
    }
  }
}
